import React, { useState, useEffect } from "react";
import axios from "axios";
import getAPI from "../../service/getAPI";

const BOOLEAN_KEYS = ["disable_auto_discount"];

const Configuration = () => {
  const { master } = getAPI();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [localValues, setLocalValues] = useState({});

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const res = await axios.post(master.configGetAll);
        if (res.data.flag === 1) {
          const data = res.data.data;
          setConfigs(data);
          const vals = {};
          data.forEach((c) => { vals[c.configuration_key] = c.configuration_value; });
          setLocalValues(vals);
        }
      } catch (err) {
        console.error("Error fetching configs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfigs();
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(localValues).map(([key, value]) => ({ key, value }));
      await axios.post(master.configUpdateBatch, { configs: payload });
      setEditingKey(null);
      alert("Settings saved successfully!");
    } catch (err) {
      console.error("Error saving configs:", err);
      alert("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRow = async (key) => {
    setSaving(true);
    try {
      await axios.post(master.configUpdateBatch, {
        configs: [{ key, value: localValues[key] }],
      });
      setEditingKey(null);
    } catch (err) {
      console.error("Error saving config:", err);
      alert("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelRow = (key, originalValue) => {
    setLocalValues((prev) => ({ ...prev, [key]: originalValue }));
    setEditingKey(null);
  };

  return (
    <main id="main" className="main">
      <div className="pagetitle d-flex justify-content-between align-items-center">
        <div>
          <h1>Configuration Settings</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">Home</li>
              <li className="breadcrumb-item active">Configuration</li>
            </ol>
          </nav>
        </div>
        <button className="btn btn-success btn-sm" onClick={handleSaveAll} disabled={saving}>
          {saving ? "Saving..." : <><i className="bi bi-floppy me-1"></i> Save All Settings</>}
        </button>
      </div>

      <section className="section">
        {/* POS Discount Settings Card */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-bottom py-3">
            <h5 className="card-title m-0 text-dark fw-bold">
              <i className="bi bi-tags-fill text-success me-2"></i> 
              POS Auto-Discount Settings
            </h5>
          </div>
          <div className="card-body pt-4">
            <div className="row g-4 align-items-center">
              <div className="col-md-6 border-end">
                <div className="d-flex justify-content-between align-items-center pe-md-4">
                  <div>
                    <h6 className="fw-bold mb-1">Automatic Qty Discount</h6>
                    <small className="text-muted">Auto calculating based on items in cart</small>
                  </div>
                  <div className="form-check form-switch fs-4 mb-0">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      checked={localValues['enable_auto_discount'] === '1' || localValues['enable_auto_discount'] === 'true'}
                      onChange={(e) => setLocalValues({ ...localValues, 'enable_auto_discount': e.target.checked ? '1' : '0' })}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="ps-md-4">
                  <h6 className="fw-bold mb-2">Default Discount Base</h6>
                  <small className="text-muted d-block mb-3">Choose whether percentage discounts apply on the MRP or on the Selling Price.</small>
                  <div className="btn-group w-100 shadow-sm" role="group">
                    <input type="radio" className="btn-check" name="baseRadio" id="baseSelling" autoComplete="off" 
                      checked={localValues['default_discount_base'] === 'sellingPrice'}
                      onChange={() => setLocalValues({ ...localValues, 'default_discount_base': 'sellingPrice' })} />
                    <label className="btn btn-outline-success fw-bold" htmlFor="baseSelling">Selling Price</label>
                    <input type="radio" className="btn-check" name="baseRadio" id="baseMrp" autoComplete="off"
                      checked={localValues['default_discount_base'] === 'mrp'}
                      onChange={() => setLocalValues({ ...localValues, 'default_discount_base': 'mrp' })} />
                    <label className="btn btn-outline-success fw-bold" htmlFor="baseMrp">MRP</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Configurations Table */}
        <div className="card">
          <div className="card-header bg-light">
            <h6 className="mb-0 fw-bold text-muted"><i className="bi bi-gear-fill me-2"></i>Advanced System Configurations</h6>
          </div>
          <div className="card-body pt-3">
            {loading ? (
              <div className="text-center py-4"><div className="spinner-border text-success"></div></div>
            ) : configs.length === 0 ? (
              <div className="text-muted text-center py-4">No configurations found.</div>
            ) : (
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th style={{ width: "30%" }}>Setting</th>
                    <th>Value</th>
                    <th style={{ width: "18%" }}>Last Updated</th>
                    <th style={{ width: "120px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map((c) => {
                    
                    const isEditing = editingKey === c.configuration_key;
                    const isBoolean = BOOLEAN_KEYS.includes(c.configuration_key);
                    const currentVal = localValues[c.configuration_key];
                    const originalVal = c.configuration_value;

                    return (
                      <tr key={c.configuration_key}>
                        <td>
                          <strong style={{ textTransform: "capitalize" }}>
                            {c.configuration_key.replace(/_/g, " ")}
                          </strong>
                        </td>
                        <td>
                          {isEditing ? (
                            isBoolean ? (
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={currentVal === "1" || currentVal === "true"}
                                  onChange={(e) =>
                                    setLocalValues({ ...localValues, [c.configuration_key]: e.target.checked ? "1" : "0" })
                                  }
                                />
                                <label className="form-check-label">
                                  {currentVal === "1" || currentVal === "true" ? "Enabled" : "Disabled"}
                                </label>
                              </div>
                            ) : (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                autoFocus
                                value={currentVal || ""}
                                onChange={(e) =>
                                  setLocalValues({ ...localValues, [c.configuration_key]: e.target.value })
                                }
                              />
                            )
                          ) : (
                            isBoolean ? (
                              <span className={`badge ${currentVal === "1" || currentVal === "true" ? "bg-success" : "bg-secondary"}`}>
                                {currentVal === "1" || currentVal === "true" ? "Enabled" : "Disabled"}
                              </span>
                            ) : (
                              <span className="text-muted" style={{ fontFamily: "monospace" }}>{currentVal || "—"}</span>
                            )
                          )}
                        </td>
                        <td className="text-muted small">
                          {new Date(c.last_updated_at).toLocaleString()}
                        </td>
                        <td>
                          {isEditing ? (
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleSaveRow(c.configuration_key)}
                                disabled={saving}
                              >
                                <i className="bi bi-check-lg"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleCancelRow(c.configuration_key, originalVal)}
                              >
                                <i className="bi bi-x-lg"></i>
                              </button>
                            </div>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setEditingKey(c.configuration_key)}
                            >
                              <i className="bi bi-pencil me-1"></i> Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Configuration;
